/**
 * L'accueil de l'espace de travail — le tableau de bord.
 *
 * La question qu'un métreur se pose en ouvrant la plateforme n'est pas « quelles
 * affaires ai-je ? » mais « laquelle demande une action ? ». Cet écran y répond,
 * à l'échelle du locataire : ce qui appelle un geste (une affaire en échec, une
 * affaire prête mais pas chiffrable faute de CCTP/DPGF, un traitement planté),
 * ce qui vient d'être touché, et le pouls des traitements de fond.
 *
 * **Il agrège, il ne mesure rien.** Tout vient d'un seul appel léger
 * (`/tableau-de-bord`) : des colonnes déjà tenues en base, aucun socle rouvert.
 */
import { api } from "../api/client";
import { CATEGORIES } from "../api/types";
import { Chargement, Echec } from "../composants/etats";
import { Etat } from "../composants/etiquettes";
import { quand } from "../composants/formats";
import { useDonnees } from "../composants/donnees";
import { Lien, naviguer } from "../routeur";

interface VueAffaire {
  affaire_id: string; reference: string; label: string | null;
  state: string; role: string | null; modifiee_le: string;
}
interface Attention extends VueAffaire { raison: string; }
interface Recente extends VueAffaire { sources: Record<string, number>; }
interface TableauDeBord {
  total: number;
  par_etat: Record<string, number>;
  attention: Attention[];
  recentes: Recente[];
  traitements: { actifs: number; echecs: number };
}

/** Les états mis en avant, dans l'ordre où ils comptent. */
const ETATS: { clef: string; mot: string }[] = [
  { clef: "READY", mot: "Prêtes" },
  { clef: "PROVISIONING", mot: "En préparation" },
  { clef: "FAILED", mot: "En échec" },
  { clef: "ARCHIVED", mot: "Archivées" },
];

const RESUMEES = ["CCTP", "PLAN_DWG", "PLAN_PDF", "DPGF"] as const;
const ABREGE: Record<string, string> = {
  CCTP: "CCTP", PLAN_DWG: "DWG", PLAN_PDF: "PDF", DPGF: "DPGF",
};

export function Accueil() {
  const tdb = useDonnees<{ donnees: TableauDeBord }>(
    () => api.lire("/api/v1/tableau-de-bord"), []);

  return (
    <div className="ecran">
      <div className="ecran__tete">
        <div className="rangee rangee--espacee rangee--bas">
          <div>
            <h1 className="ecran__titre">Tableau de bord</h1>
            <div className="ecran__compte">
              {tdb.donnees
                ? `${tdb.donnees.donnees.total} affaire${tdb.donnees.donnees.total > 1 ? "s" : ""} dans l'espace`
                : "Espace de travail"}
            </div>
          </div>
          <Lien vers="/affaires/nouvelle" className="bouton bouton--primaire">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Démarrer une affaire
          </Lien>
        </div>
      </div>

      <div className="ecran__corps pile">
        {tdb.charge && !tdb.donnees ? (
          <Chargement lignes={4} />
        ) : tdb.erreur ? (
          <Echec erreur={tdb.erreur} reessayer={tdb.recharger} />
        ) : tdb.donnees ? (
          <Corps tdb={tdb.donnees.donnees} />
        ) : null}
      </div>
    </div>
  );
}

function Corps({ tdb }: { tdb: TableauDeBord }) {
  return (
    <>
      {/* Les cartes d'état : un coup d'œil sur le portefeuille, chacune mène à
          sa liste filtrée. Le pouls des traitements de fond en dernière carte. */}
      <div className="acc-cartes">
        {ETATS.map(({ clef, mot }) => {
          const n = tdb.par_etat[clef] ?? 0;
          return (
            <button key={clef} type="button"
                    className={`acc-carte acc-carte--${clef.toLowerCase()}`}
                    onClick={() => naviguer(`/affaires?etat=${clef}`)}>
              <span className="acc-carte__n nombre">{n}</span>
              <span className="acc-carte__mot">{mot}</span>
            </button>
          );
        })}
        <button type="button"
                className={`acc-carte acc-carte--traitements${tdb.traitements.echecs ? " acc-carte--alerte" : ""}`}
                onClick={() => naviguer("/traitements")}>
          <span className="acc-carte__n nombre">{tdb.traitements.actifs}</span>
          <span className="acc-carte__mot">
            Traitements en cours
            {tdb.traitements.echecs
              ? ` · ${tdb.traitements.echecs} en échec` : ""}
          </span>
        </button>
      </div>

      {/* À regarder : ce qui appelle un geste. Vide, on le dit — c'est une
          bonne nouvelle, pas un écran cassé. */}
      <div className="carte">
        <div className="carte__corps pile pile--serree">
          <h3 style={{ margin: 0 }}>À regarder</h3>
          {tdb.attention.length === 0 ? (
            <span className="discret">
              Rien ne demande d'action : aucune affaire en échec, et les affaires
              prêtes portent leur CCTP et leur DPGF.
            </span>
          ) : (
            <div className="acc-liste">
              {tdb.attention.map((a) => (
                <button key={a.affaire_id} type="button" className="acc-ligne"
                        onClick={() => naviguer(`/affaires/${a.affaire_id}`)}>
                  <span className="mono">{a.reference}</span>
                  <span className="tronque">
                    {a.label || <span className="faible">Sans intitulé</span>}
                  </span>
                  <span className={`acc-raison acc-raison--${a.state === "FAILED" ? "echec" : "manque"}`}>
                    {a.raison}
                  </span>
                  <Etat genre="affaire" valeur={a.state} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Récemment : reprendre là où l'on s'est arrêté. */}
      <div className="carte">
        <div className="carte__corps pile pile--serree">
          <div className="rangee rangee--espacee">
            <h3 style={{ margin: 0 }}>Récemment</h3>
            <Lien vers="/affaires" className="discret">Toutes les affaires →</Lien>
          </div>
          {tdb.recentes.length === 0 ? (
            <span className="discret">Aucune affaire encore.</span>
          ) : (
            <div className="acc-liste">
              {tdb.recentes.map((a) => (
                <button key={a.affaire_id} type="button" className="acc-ligne"
                        onClick={() => naviguer(`/affaires/${a.affaire_id}`)}>
                  <span className="mono">{a.reference}</span>
                  <span className="tronque">
                    {a.label || <span className="faible">Sans intitulé</span>}
                  </span>
                  <span className="sources">
                    {RESUMEES.map((clef) => {
                      const nombre = a.sources[clef] ?? 0;
                      const libelle =
                        CATEGORIES.find((c) => c.clef === clef)?.libelle ?? clef;
                      return (
                        <span key={clef}
                              className={`sources__pastille${nombre ? " sources__pastille--pleine" : ""}`}
                              title={`${libelle} : ${nombre || "aucune"}`}>
                          {ABREGE[clef]}
                        </span>
                      );
                    })}
                  </span>
                  <span className="discret">{quand(a.modifiee_le)}</span>
                  <Etat genre="affaire" valeur={a.state} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
